import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
    Zap,
    Code2,
    Boxes,
    TrendingUp,
    CheckCircle,
    Layers,
    Globe,
    Smartphone,
    ArrowRight,
    Telescope
} from "lucide-react";

export default function ReactTech() {
    const navigate = useNavigate();
    const features = [
        {
            icon: <Zap className="w-6 h-6" />,
            title: "Performance Optimale",
            description: "Virtual DOM pour des mises à jour ultra-rapides et une expérience utilisateur fluide"
        },
        {
            icon: <Boxes className="w-6 h-6" />,
            title: "Architecture Composable",
            description: "Composants réutilisables pour un code maintenable et scalable"
        },
        {
            icon: <Globe className="w-6 h-6" />,
            title: "Écosystème Riche",
            description: "Des milliers de bibliothèques et outils pour accélérer le développement"
        },
        {
            icon: <Smartphone className="w-6 h-6" />,
            title: "React Native",
            description: "Une codebase pour iOS et Android avec performances natives"
        }
    ];

    const useCases = [
        "Applications web single-page (SPA) hautement interactives",
        "Interfaces utilisateur complexes avec états dynamiques",
        "Dashboards et panneaux d'administration modernes",
        "Applications e-commerce avec expérience utilisateur premium",
        "Progressive Web Apps (PWA) performantes"
    ];

    const whyWeUse = [
        {
            title: "Leader du marché",
            description: "Utilisé par Facebook, Insta gram, Netflix, et des milliers d'entreprises"
        },
        {
            title: "Communauté active",
            description: "Support constant, innovations régulières et ressources abondantes"
        },
        {
            title: "Flexibilité maximale",
            description: "S'adapte à tous types de projets, du prototype au système d'entreprise"
        }
    ];

    return (
        <div className="space-y-24">
            {/* Hero Section */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-900/30 to-cyan-900/30 backdrop-blur-xl border border-blue-500/20 p-12 md:p-16"
            >
                {/* Effet de fond animé */}
                <motion.div
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.5, 0.3]
                    }}
                    transition={{ duration: 8, repeat: Infinity }}
                    className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"
                />

                <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3, type: "spring" }}
                            className="inline-flex items-center gap-3 px-4 py-2 bg-blue-500/20 rounded-full mb-6 border border-blue-400/30"
                        >
                            <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse" />
                            <span className="text-blue-300 font-semibold text-sm">Bibliothèque JavaScript #1</span>
                        </motion.div>

                        <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
                            React
                        </h2>

                        <p className="text-xl md:text-2xl text-cyan-300 mb-6 font-semibold">
                            La bibliothèque JavaScript pour créer des interfaces utilisateur modernes
                        </p>

                        <p className="text-gray-300 text-md md:text-lg leading-relaxed mb-8">
                            React révolutionne le développement front-end avec son approche basée sur les composants
                            et son Virtual DOM. Nous l'utilisons pour créer des interfaces ultra-réactives,
                            maintenables et performantes.
                        </p>

                        <div className="flex flex-wrap gap-3">
                            {["Component-Based", "Virtual DOM", "JSX", "Hooks"].map((tag, i) => (
                                <motion.span
                                    key={i}
                                    initial={{ opacity: 0, scale: 0 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.5 + i * 0.1 }}
                                    className="px-4 py-2 bg-blue-500/10 border border-blue-400/20 rounded-full text-blue-300 text-[14px] md:text-sm font-medium"
                                >
                                    {tag}
                                </motion.span>
                            ))}
                        </div>
                    </div>

                    {/* Logo React animé */}
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                        className="flex items-center justify-center"
                    >
                        <div className="relative w-64 h-64">
                            {/* Orbites */}
                            {[0, 60, 120].map((rotation, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute inset-0 border-2 border-blue-400/30 rounded-full"
                                    style={{
                                        transform: `rotate(${rotation}deg) scaleY(0.3)`,
                                    }}
                                    animate={{
                                        borderColor: ['rgba(96, 165, 250, 0.3)', 'rgba(34, 211, 238, 0.5)', 'rgba(96, 165, 250, 0.3)']
                                    }}
                                    transition={{ duration: 3, repeat: Infinity, delay: i * 0.5 }}
                                />
                            ))}
                            {/* Centre */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <motion.div
                                    className="w-16 h-16 bg-blue-400 rounded-full shadow-lg shadow-blue-500/50"
                                    animate={{
                                        scale: [1, 1.2, 1],
                                        boxShadow: [
                                            '0 0 20px rgba(96, 165, 250, 0.5)',
                                            '0 0 40px rgba(34, 211, 238, 0.7)',
                                            '0 0 20px rgba(96, 165, 250, 0.5)'
                                        ]
                                    }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                >
                                    <div className="text-white font-bold text-4xl absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">R</div>
                                </motion.div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </motion.div>

            {/* Points Forts */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
            >
                <h3 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-12 text-center">
                    Points Forts de<span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-600 bg-clip-text text-transparent px-2 sm:px-4">React</span>
                </h3>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {features.map((feature, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * index }}
                            whileHover={{
                                scale: 1.05,
                                y: -5,
                                transition: { type: "spring", stiffness: 400 }
                            }}
                            className="bg-gradient-to-br from-blue-900/20 to-cyan-900/20 backdrop-blur-xl rounded-2xl p-6 border border-blue-500/20 hover:border-blue-400/40 transition-all duration-300 group"
                        >
                            <motion.div
                                whileHover={{ scale: 1.1, rotate: 5 }}
                                className="w-14 h-14 bg-gradient-to-br from-blue-500/30 to-cyan-500/30 text-center rounded-xl flex items-center justify-center mb-4 group-hover:shadow-xl group-hover:shadow-blue-500/30 transition-all duration-300"
                            >
                                <div className="text-blue-400 text-center">
                                    {feature.icon}
                                </div>
                            </motion.div>

                            <h4 className="text-xl font-bold text-white mb-2">
                                {feature.title}
                            </h4>
                            <p className="text-gray-300">
                                {feature.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Cas d'Usage */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="grid lg:grid-cols-2 gap-12"
            >
                <div>
                    <h3 className="text-3xl font-bold text-white mb-6">
                        Cas d'Usage Idéaux
                    </h3>
                    <div className="space-y-4">
                        {useCases.map((useCase, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.6 + index * 0.1 }}
                                className="flex items-start gap-3 group"
                            >
                                <motion.div
                                    whileHover={{ scale: 1.2, rotate: 90 }}
                                    className="mt-1"
                                >
                                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                                </motion.div>
                                <p className="text-gray-300 group-hover:text-white transition-colors">
                                    {useCase}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                <div>
                    <h3 className="text-3xl font-bold text-white mb-6">
                        Pourquoi Nous Utilisons React
                    </h3>
                    <div className="space-y-6">
                        {whyWeUse.map((reason, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.7 + index * 0.1 }}
                                whileHover={{ x: 10 }}
                                className="bg-gradient-to-r from-blue-500/10 to-transparent backdrop-blur-sm rounded-xl p-6 border-l-4 border-blue-500"
                            >
                                <h4 className="text-xl font-bold text-blue-300 mb-2">
                                    {reason.title}
                                </h4>
                                <p className="text-gray-300">
                                    {reason.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.div>

            {/* Stack Complémentaire */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="bg-gradient-to-br from-blue-900/20 to-cyan-900/20 backdrop-blur-xl rounded-3xl p-12 border border-blue-500/20"
            >
                <h3 className="text-3xl font-bold text-white mb-8 text-center">
                    Notre Stack React Premium
                </h3>

                <div className="grid md:grid-cols-3 gap-6">
                    {[
                        { name: "Next.js", desc: "Framework React pour production", color: "from-gray-700 to-black" },
                        { name: "TypeScript", desc: "Typage statique robuste", color: "from-blue-600 to-blue-800" },
                        { name: "Tailwind CSS", desc: "Styling moderne et rapide", color: "from-cyan-500 to-blue-500" },
                        { name: "Framer Motion", desc: "Animations fluides", color: "from-pink-500 to-purple-500" },
                        { name: "React Query", desc: "Gestion d'état serveur", color: "from-red-500 to-orange-500" },
                        { name: "Zustand", desc: "State management simple", color: "from-amber-500 to-yellow-500" }
                    ].map((tool, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.9 + index * 0.05 }}
                            whileHover={{ scale: 1.05 }}
                            className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-blue-500/30 transition-all duration-300"
                        >
                            <div className={`h-2 w-16 bg-gradient-to-r ${tool.color} rounded-full mb-4`} />
                            <h4 className="text-lg font-bold text-white mb-2">{tool.name}</h4>
                            <p className="text-gray-400 text-sm">{tool.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* CTA */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="text-center flex flex-col items-center justify-center bg-gradient-to-r from-blue-900/30 to-cyan-900/30 backdrop-blur-xl rounded-3xl p-12 border border-blue-500/20"
            >
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    Prêt à Créer Votre Application React ?
                </h3>
                <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                    Discutons de votre projet et créons ensemble une expérience utilisateur exceptionnelle
                </p>
                <motion.button
                    onClick={() => navigate("/Contact")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-[250px] sm:w-[320px] cursor-pointer px-6 py-4 sm:px-8 sm:py-5  text-center text-[14px] sm:text-lg font-medium flex items-center gap-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-xl shadow-xl hover:shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 cursor-pointer"
                >
                    <span>Démarrer un projet React</span>
                    <Telescope className="w-5 h-5 md:w-6 md:h-6" />
                </motion.button>
            </motion.div>
        </div>
    );
}
